// Dashboard.jsx (updated with modern design)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../../context/StudentAuthContext';
import toast from 'react-hot-toast';
import StatCard from '../../components/common/StatCard';
import ChartComponent from '../../components/common/ChartComponent';
import { Link } from 'react-router-dom';

const StudentDashboard = () => {
  const { student, studentApi } = useStudentAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    profile: null,
    performanceSummary: null,
    recentGrades: [],
    subjects: [],
    upcomingAssessments: [],
    subjectPerformances: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [profileRes, summaryRes, sessionsRes, subjectsRes, assessmentsRes] = await Promise.all([
        studentApi.get('/student-portal/profile'),
        studentApi.get('/student-portal/performance-summary'),
        studentApi.get('/student-portal/academic-sessions'),
        studentApi.get('/student-portal/subjects'),
        studentApi.get('/student-portal/assessments?limit=5')
      ]);

      // Get subject performances for all subjects
      const subjectPerformancePromises = (subjectsRes.data || []).map(async (subject) => {
        try {
          const performanceRes = await studentApi.get(`/student-portal/subject-performance/${subject.id}`);
          return {
            ...subject,
            performanceHistory: performanceRes.data?.performance_history || [],
            statistics: performanceRes.data?.statistics || {},
            latest_score: performanceRes.data?.statistics?.average_score || null
          };
        } catch (error) {
          return { 
            ...subject, 
            performanceHistory: [], 
            statistics: {},
            latest_score: null 
          };
        }
      });

      const subjectPerformances = await Promise.all(subjectPerformancePromises);

      setDashboardData({
        profile: profileRes.data || {},
        performanceSummary: summaryRes.data || {},
        subjects: subjectsRes.data || [],
        recentGrades: summaryRes.data?.recent_grades || [],
        upcomingAssessments: assessmentsRes.data?.filter(a => new Date(a.due_date) > new Date()) || [],
        subjectPerformances
      });

      setSessions(sessionsRes.data || []);
      
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fixed: Safely handle missing performance_history by using average_score
  const getPerformanceChartData = () => {
    const { performanceSummary } = dashboardData;
    
    // If we have performance history, use it
    if (performanceSummary?.performance_history?.length > 0) {
      const history = performanceSummary.performance_history.slice(0, 6);
      
      const labels = history.map(item => 
        `${item.term || 'Term'} - ${item.academic_session || 'Session'}`
      );
      
      const scores = history.map(item => item.average || item.score || 0);
      
      return {
        labels,
        datasets: [
          {
            label: 'Average Score',
            data: scores,
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#6366f1',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
          }
        ]
      };
    }
    
    // Fallback: Use current average score as a single data point
    const avgScore = performanceSummary?.statistics?.average_score || 0;
    
    return {
      labels: ['Current Average'],
      datasets: [
        {
          label: 'Average Score',
          data: [avgScore],
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
        }
      ]
    };
  };

  const getSubjectPerformanceData = () => {
    const { subjectPerformances } = dashboardData;
    
    if (!subjectPerformances.length) return null;
    
    // Get top 5 subjects by latest score
    const topSubjects = [...subjectPerformances]
      .filter(subject => subject.latest_score != null)
      .sort((a, b) => (b.latest_score || 0) - (a.latest_score || 0))
      .slice(0, 5);
    
    if (topSubjects.length === 0) return null;
    
    return {
      labels: topSubjects.map(subject => subject.subject_name || subject.subject_code),
      datasets: [
        {
          label: 'Latest Score',
          data: topSubjects.map(subject => subject.latest_score || 0),
          backgroundColor: [
            'rgba(99, 102, 241, 0.8)',
            'rgba(14, 165, 233, 0.8)',
            'rgba(34, 197, 94, 0.8)',
            'rgba(234, 179, 8, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            '#6366f1',
            '#0ea5e9',
            '#22c55e',
            '#eab308',
            '#ef4444'
          ],
          borderWidth: 2,
          borderRadius: 6,
          barPercentage: 0.7,
        }
      ]
    };
  };

  // Alternative: Create grade distribution chart if available
  const getGradeDistributionData = () => {
    const { performanceSummary } = dashboardData;
    const gradeDist = performanceSummary?.grade_distribution || {};
    
    if (Object.keys(gradeDist).length === 0) return null;
    
    return {
      labels: Object.keys(gradeDist),
      datasets: [
        {
          label: 'Percentage of Subjects',
          data: Object.values(gradeDist),
          backgroundColor: [
            '#16a34a', '#22c55e', '#3b82f6', '#facc15', '#f97316', '#ef4444'
          ].slice(0, Object.keys(gradeDist).length),
          borderWidth: 1,
          borderRadius: 4,
        }
      ]
    };
  };

  const getGradeColor = (score) => {
    if (!score && score !== 0) return 'text-gray-600';
    if (score >= 90) return 'text-emerald-600';
    if (score >= 80) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getGradeBadge = (score) => {
    if (!score && score !== 0) return { text: 'N/A', color: 'bg-gray-100 text-gray-800' };
    if (score >= 90) return { text: 'A+', color: 'bg-emerald-100 text-emerald-800' };
    if (score >= 80) return { text: 'A', color: 'bg-green-100 text-green-800' };
    if (score >= 70) return { text: 'B', color: 'bg-blue-100 text-blue-800' };
    if (score >= 60) return { text: 'C', color: 'bg-yellow-100 text-yellow-800' };
    if (score >= 50) return { text: 'D', color: 'bg-orange-100 text-orange-800' };
    return { text: 'F', color: 'bg-red-100 text-red-800' };
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mb-4"></div>
        <p className="text-gray-600 animate-pulse">Loading your dashboard...</p>
      </div>
    );
  }

  const { profile, performanceSummary, recentGrades, subjectPerformances, upcomingAssessments } = dashboardData;

  const currentSession = sessions.find(s => s.id === profile?.academic_session_id);
  const currentTerm = currentSession?.terms?.find(t => t.term_status === 'published')?.term;

  // Choose which chart data to display
  const performanceChartData = getPerformanceChartData();
  const gradeDistributionData = getGradeDistributionData();
  const subjectChartData = getSubjectPerformanceData();

// Helper to get full image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `https://multi-schoolsuite.cbthost.com${path}`;
  };

    // Get passport URL from custom_fields
  const passportUrl = profile?.custom_fields?.passport ? getImageUrl(profile.custom_fields.passport) : null;


  return (
    <div className="space-y-8">
      {/* Welcome Banner - Modern Design */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 p-8 text-white shadow-xl">
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Welcome back, {profile?.surname || 'Student'}!
              </h1>
              <p className="opacity-90 text-lg">
                Stay updated with your academic progress
              </p>
              <div className="flex flex-wrap gap-4 mt-6">
                <div className="flex items-center space-x-2">
                  <i className="fas fa-id-card text-indigo-200"></i>
                  <span className="font-medium">{profile?.student_reg || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-school text-indigo-200"></i>
                  <span>{profile?.class_name || 'Not assigned'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-calendar-alt text-indigo-200"></i>
                  <span>
                    {currentSession?.academic_session || 'Current Session'} - {currentTerm || 'Term'}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6 md:mt-0">
<div className="relative w-24 h-24 rounded-full overflow-hidden bg-white/10 backdrop-blur-sm flex items-center justify-center">
  {passportUrl ? (
    <img
      src={passportUrl}
      alt="Passport"
      className="w-full h-full object-cover rounded-full"
    />
  ) : (
    <i className="fas fa-user-graduate text-4xl text-white/80"></i>
  )}
</div>
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Current Average"
          value={performanceSummary?.statistics?.average_score?.toFixed(1) || '0.0'}
          icon="fa-chart-line"
          color="indigo"
          suffix="/100"
          trend="+2.5%"
          link="/student/grades"
          gradient
        />
        <StatCard
          title="Best Position"
          value={performanceSummary?.statistics?.best_position || 'N/A'}
          icon="fa-trophy"
          color="amber"
          suffix="th"
          trend="↑ 3 ranks"
        />
        <StatCard
          title="Active Subjects"
          value={subjectPerformances.length || 0}
          icon="fa-book-open"
          color="emerald"
          link="/student/grades#subjects"
          trend={`${subjectPerformances.filter(s => s.latest_score >= 70).length || 0} excelling`}
        />
        <StatCard
          title="Attendance"
          value="%"
          icon="fa-calendar-check"
          color="blue"
          trend="Consistent"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Performance Overview</h3>
              <p className="text-gray-600 text-sm">
                {performanceSummary?.performance_history?.length > 0 
                  ? 'Your progress over time' 
                  : 'Current average score'}
              </p>
            </div>
            <button 
              onClick={fetchDashboardData}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors"
            >
              <i className="fas fa-sync-alt"></i>
              <span>Refresh</span>
            </button>
          </div>
          
          <div className="h-72">
            {performanceChartData ? (
              <ChartComponent
                type="line"
                data={performanceChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: performanceChartData.labels.length > 1
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      cornerRadius: 8,
                      callbacks: {
                        label: (context) => `Score: ${context.raw}%`
                      }
                    }
                  },
                  scales: {
                    y: {
                      min: 0,
                      max: 100,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.05)'
                      },
                      ticks: {
                        callback: (value) => `${value}%`
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <i className="fas fa-chart-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 font-medium">No performance data yet</p>
                <p className="text-gray-400 text-sm">Complete your first assessment to see trends</p>
              </div>
            )}
          </div>
          
          {/* Optional: Show grade distribution if available */}
          {gradeDistributionData && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Grade Distribution</h4>
              <div className="h-48">
                <ChartComponent
                  type="bar"
                  data={gradeDistributionData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.raw}% of subjects`
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: { callback: (value) => `${value}%` }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Upcoming Assessments */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Upcoming</h3>
              <p className="text-gray-600 text-sm">Tests & assignments</p>
            </div>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {upcomingAssessments.length} due
            </span>
          </div>
          <div className="space-y-4">
            {upcomingAssessments.length > 0 ? (
              upcomingAssessments.slice(0, 4).map((assessment, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-xl hover:border-indigo-300 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-gray-900">{assessment.subject_name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{assessment.type || 'Assessment'}</p>
                      <div className="flex items-center space-x-3 mt-2">
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded-md">
                          <i className="far fa-calendar mr-1"></i>
                          {new Date(assessment.due_date).toLocaleDateString()}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-md ${
                          assessment.priority === 'high' ? 'bg-red-100 text-red-800' :
                          assessment.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {assessment.priority || 'normal'}
                        </span>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-indigo-600 transition-colors">
                      <i className="far fa-bell"></i>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <i className="fas fa-clipboard-check text-4xl text-gray-300 mb-3"></i>
                <p className="text-gray-500 font-medium">No upcoming assessments</p>
                <p className="text-gray-400 text-sm mt-1">Enjoy your free time!</p>
              </div>
            )}
            {upcomingAssessments.length > 4 && (
              <button className="w-full py-3 text-center text-indigo-600 hover:text-indigo-700 font-medium rounded-lg hover:bg-indigo-50 transition-colors">
                View all {upcomingAssessments.length} assessments
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Subject Performance */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Subject Performance</h3>
              <p className="text-gray-600 text-sm">Your scores across all subjects</p>
            </div>
            <div className="flex space-x-2">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                This Term
              </button>
              <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors">
                All Time
              </button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Subject Chart */}
          <div>
            <div className="h-72">
              {subjectChartData ? (
                <ChartComponent
                  type="bar"
                  data={subjectChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12,
                        cornerRadius: 8,
                        callbacks: {
                          label: (context) => `Score: ${context.raw}%`
                        }
                      }
                    },
                    scales: {
                      y: {
                        min: 0,
                        max: 100,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)'
                        },
                        ticks: {
                          callback: (value) => `${value}%`
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <i className="fas fa-book text-5xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 font-medium">No subject data available</p>
                    <p className="text-gray-400 text-sm">Subjects will appear after enrollment</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subject List */}
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subjectPerformances.slice(0, 6).map((subject, index) => {
                    const gradeBadge = getGradeBadge(subject.latest_score);
                    return (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center">
                              <i className="fas fa-book text-indigo-600"></i>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-900">
                                {subject.subject_name || `Subject ${index + 1}`}
                              </div>
                              <div className="text-xs text-gray-500">
                                {subject.subject_code || 'No code'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-lg font-bold ${getGradeColor(subject.latest_score)}`}>
                            {subject.latest_score?.toFixed(1) || 'N/A'}
                            {subject.latest_score && '/100'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${gradeBadge.color}`}>
                            {gradeBadge.text}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-2 w-16 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${subject.latest_score ? getGradeColor(subject.latest_score).replace('text-', 'bg-') : 'bg-gray-400'}`}
                                style={{ width: `${subject.latest_score || 0}%` }}
                              ></div>
                            </div>
                            <span className="ml-2 text-sm text-gray-500">
                              {subject.total_assessments || 0} assessments
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {subjectPerformances.length > 6 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <button className="w-full py-2 text-center text-indigo-600 hover:text-indigo-700 font-medium rounded-lg hover:bg-indigo-50 transition-colors">
                  Show all {subjectPerformances.length} subjects
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-8 text-white">
        <h3 className="text-2xl font-bold mb-6">Quick Access</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            style={{ textDecoration: "none" }}
            to="/student/grades"
            className="group p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-indigo-500/20 rounded-lg group-hover:bg-indigo-500/30 transition-colors">
                <i className="fas fa-graduation-cap text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-2">Detailed Grades</h4>
                <p className="text-white/80 text-sm">View complete academic history with detailed breakdowns</p>
              </div>
            </div>
          </Link>
          
          <Link
            style={{ textDecoration: "none" }}
            to="/student/grades#assessments"
            className="group p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-emerald-500/20 rounded-lg group-hover:bg-emerald-500/30 transition-colors">
                <i className="fas fa-chart-bar text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-2">Assessment Analytics</h4>
                <p className="text-white/80 text-sm">Deep dive into test scores and performance metrics</p>
              </div>
            </div>
          </Link>
          
          <Link
            style={{ textDecoration: "none" }}
            to="/student/profile"
            className="group p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-purple-500/20 rounded-lg group-hover:bg-purple-500/30 transition-colors">
                <i className="fas fa-user-cog text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-2">Profile Settings</h4>
                <p className="text-white/80 text-sm">Update personal information and preferences</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;

// // Dashboard.jsx (updated with modern design)
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useStudentAuth } from '../../context/StudentAuthContext';
// import toast from 'react-hot-toast';
// import StatCard from '../../components/common/StatCard';
// import ChartComponent from '../../components/common/ChartComponent';
// import { Link } from 'react-router-dom';

// const StudentDashboard = () => {
//   const { student, studentApi } = useStudentAuth();
//   const navigate = useNavigate();
//   const [dashboardData, setDashboardData] = useState({
//     profile: null,
//     performanceSummary: null,
//     recentGrades: [],
//     subjects: [],
//     upcomingAssessments: [],
//     subjectPerformances: []
//   });
//   const [loading, setLoading] = useState(true);
//   const [selectedSession, setSelectedSession] = useState(null);
//   const [sessions, setSessions] = useState([]);

//   useEffect(() => {
//     fetchDashboardData();
//   }, []);

//   const fetchDashboardData = async () => {
//     try {
//       setLoading(true);
      
//       // Fetch all data in parallel
//       const [profileRes, summaryRes, sessionsRes, subjectsRes, assessmentsRes] = await Promise.all([
//         studentApi.get('/student-portal/profile'),
//         studentApi.get('/student-portal/performance-summary'),
//         studentApi.get('/student-portal/academic-sessions'),
//         studentApi.get('/student-portal/subjects'),
//         studentApi.get('/student-portal/assessments?limit=5')
//       ]);

//       // Get subject performances for all subjects
//       const subjectPerformancePromises = (subjectsRes.data || []).map(async (subject) => {
//         try {
//           const performanceRes = await studentApi.get(`/student-portal/subject-performance/${subject.id}`);
//           return {
//             ...subject,
//             performanceHistory: performanceRes.data?.performance_history || [],
//             statistics: performanceRes.data?.statistics || {}
//           };
//         } catch (error) {
//           return { ...subject, performanceHistory: [], statistics: {} };
//         }
//       });

//       const subjectPerformances = await Promise.all(subjectPerformancePromises);

//       setDashboardData({
//         profile: profileRes.data,
//         performanceSummary: summaryRes.data,
//         subjects: subjectsRes.data || [],
//         recentGrades: summaryRes.data?.recent_grades || [],
//         upcomingAssessments: assessmentsRes.data?.filter(a => new Date(a.due_date) > new Date()) || [],
//         subjectPerformances
//       });

//       setSessions(sessionsRes.data || []);
      
//     } catch (error) {
//       console.error('Dashboard error:', error);
//       toast.error(error.response?.data?.detail || 'Failed to load dashboard data');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const getPerformanceChartData = () => {
//     const { performanceSummary } = dashboardData;
    
//     if (!performanceSummary?.performance_history) return null;
    
//     const history = performanceSummary.performance_history.slice(0, 6);
    
//     const labels = history.map(item => 
//       `${item.term || 'Term'} - ${item.academic_session || 'Session'}`
//     );
    
//     const scores = history.map(item => item.average || item.score || 0);
    
//     return {
//       labels,
//       datasets: [
//         {
//           label: 'Average Score',
//           data: scores,
//           borderColor: '#6366f1',
//           backgroundColor: 'rgba(99, 102, 241, 0.1)',
//           tension: 0.4,
//           fill: true,
//           pointBackgroundColor: '#6366f1',
//           pointBorderColor: '#fff',
//           pointBorderWidth: 2,
//           pointRadius: 4,
//         }
//       ]
//     };
//   };

//   const getSubjectPerformanceData = () => {
//     const { subjectPerformances } = dashboardData;
    
//     if (!subjectPerformances.length) return null;
    
//     // Get top 5 subjects by latest score
//     const topSubjects = [...subjectPerformances]
//       .filter(subject => subject.latest_score != null)
//       .sort((a, b) => (b.latest_score || 0) - (a.latest_score || 0))
//       .slice(0, 5);
    
//     return {
//       labels: topSubjects.map(subject => subject.subject_name || subject.subject_code),
//       datasets: [
//         {
//           label: 'Latest Score',
//           data: topSubjects.map(subject => subject.latest_score || 0),
//           backgroundColor: [
//             'rgba(99, 102, 241, 0.8)',
//             'rgba(14, 165, 233, 0.8)',
//             'rgba(34, 197, 94, 0.8)',
//             'rgba(234, 179, 8, 0.8)',
//             'rgba(239, 68, 68, 0.8)'
//           ],
//           borderColor: [
//             '#6366f1',
//             '#0ea5e9',
//             '#22c55e',
//             '#eab308',
//             '#ef4444'
//           ],
//           borderWidth: 2,
//           borderRadius: 6,
//           barPercentage: 0.7,
//         }
//       ]
//     };
//   };

//   const getGradeColor = (score) => {
//     if (score >= 90) return 'text-emerald-600';
//     if (score >= 80) return 'text-green-600';
//     if (score >= 70) return 'text-blue-600';
//     if (score >= 60) return 'text-yellow-600';
//     if (score >= 50) return 'text-orange-600';
//     return 'text-red-600';
//   };

//   const getGradeBadge = (score) => {
//     if (score >= 90) return { text: 'A+', color: 'bg-emerald-100 text-emerald-800' };
//     if (score >= 80) return { text: 'A', color: 'bg-green-100 text-green-800' };
//     if (score >= 70) return { text: 'B', color: 'bg-blue-100 text-blue-800' };
//     if (score >= 60) return { text: 'C', color: 'bg-yellow-100 text-yellow-800' };
//     if (score >= 50) return { text: 'D', color: 'bg-orange-100 text-orange-800' };
//     return { text: 'F', color: 'bg-red-100 text-red-800' };
//   };

//   if (loading) {
//     return (
//       <div className="flex flex-col items-center justify-center h-96">
//         <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mb-4"></div>
//         <p className="text-gray-600 animate-pulse">Loading your dashboard...</p>
//       </div>
//     );
//   }

//   const { profile, performanceSummary, recentGrades, subjectPerformances, upcomingAssessments } = dashboardData;

//   const currentSession = sessions.find(s => s.id === profile?.academic_session_id);
// const currentTerm = currentSession?.terms?.find(t => t.term_status === 'published')?.term;

//   return (
//     <div className="space-y-8">
//       {/* Welcome Banner - Modern Design */}
//       <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 p-8 text-white shadow-xl">
//         <div className="relative z-10">
//           <div className="flex flex-col md:flex-row md:items-center md:justify-between">
//             <div>
//               <h1 className="text-3xl font-bold mb-2">
//                 Welcome back, {profile?.surname}!
//               </h1>
//               <p className="opacity-90 text-lg">
//                 Stay updated with your academic progress
//               </p>
//               <div className="flex flex-wrap gap-4 mt-6">
//                 <div className="flex items-center space-x-2">
//                   <i className="fas fa-id-card text-indigo-200"></i>
//                   <span className="font-medium">{profile?.student_reg}</span>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <i className="fas fa-school text-indigo-200"></i>
//                   <span>{profile?.class_name || 'Not assigned'}</span>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <i className="fas fa-calendar-alt text-indigo-200"></i>
//                   {/* <span>{profile?.academic_session || 'Current Session'}</span> */}
//                   {/* <span>
//   {sessions.find(s => s.id === profile?.academic_session_id)?.academic_session || 'Current Session'}
// </span> */}
// <span>
//   {currentSession?.academic_session} - {currentTerm || 'Term'}
// </span>

//                 </div>
//               </div>
//             </div>
//             <div className="mt-6 md:mt-0">
//               <div className="relative w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
//                 <i className="fas fa-user-graduate text-4xl text-white/80"></i>
//               </div>
//             </div>
//           </div>
//         </div>
//         {/* Decorative elements */}
//         <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
//         <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
//       </div>

//       {/* Quick Stats Grid */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
//         <StatCard
//           title="Current Average"
//           value={performanceSummary?.statistics?.average_score?.toFixed(1) || '0.0'}
//           icon="fa-chart-line"
//           color="indigo"
//           suffix="/100"
//           trend="+2.5%"
//           link="/student/grades"
//           gradient
//         />
//         <StatCard
//           title="Best Position"
//           value={performanceSummary?.statistics?.best_position || 'N/A'}
//           icon="fa-trophy"
//           color="amber"
//           suffix="th"
//           trend="↑ 3 ranks"
//         />
//         <StatCard
//           title="Active Subjects"
//           value={subjectPerformances.length || 0}
//           icon="fa-book-open"
//           color="emerald"
//           link="/student/grades#subjects"
//           trend={`${subjectPerformances.filter(s => s.latest_score >= 70).length || 0} excelling`}
//         />
//         <StatCard
//           title="Attendance"
//           value="96%"
//           icon="fa-calendar-check"
//           color="blue"
//           trend="Consistent"
//         />
//       </div>

//       {/* Main Content Grid */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//         {/* Performance Chart */}
//         <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
//           <div className="flex justify-between items-center mb-6">
//             <div>
//               <h3 className="text-xl font-bold text-gray-900">Performance Trend</h3>
//               <p className="text-gray-600 text-sm">Your progress over time</p>
//             </div>
//             <button 
//               onClick={fetchDashboardData}
//               className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors"
//             >
//               <i className="fas fa-sync-alt"></i>
//               <span>Refresh</span>
//             </button>
//           </div>
//           {/* <div className="h-72">
//             {recentGrades.length > 0 ? (
//               <ChartComponent
//                 type="line"
//                 data={getPerformanceChartData()}
//                 options={{
//                   responsive: true,
//                   maintainAspectRatio: false,
//                   plugins: {
//                     legend: {
//                       display: false
//                     },
//                     tooltip: {
//                       backgroundColor: 'rgba(0, 0, 0, 0.8)',
//                       padding: 12,
//                       cornerRadius: 8,
//                       callbacks: {
//                         label: (context) => `Score: ${context.raw}%`
//                       }
//                     }
//                   },
//                   scales: {
//                     y: {
//                       min: 0,
//                       max: 100,
//                       grid: {
//                         color: 'rgba(0, 0, 0, 0.05)'
//                       },
//                       ticks: {
//                         callback: (value) => `${value}%`
//                       }
//                     },
//                     x: {
//                       grid: {
//                         display: false
//                       }
//                     }
//                   }
//                 }}
//               />
//             ) : (
//               <div className="flex flex-col items-center justify-center h-full">
//                 <i className="fas fa-chart-line text-5xl text-gray-300 mb-4"></i>
//                 <p className="text-gray-500 font-medium">No performance data yet</p>
//                 <p className="text-gray-400 text-sm">Complete your first assessment to see trends</p>
//               </div>
//             )}
//           </div> */}


//           <div className="h-72">
//   {subjectPerformances.length > 0 ? (
//     <ChartComponent
//       type="line"
//       data={getPerformanceChartData()}
//       options={{
//         responsive: true,
//         maintainAspectRatio: false,
//         plugins: {
//           legend: { display: false },
//           tooltip: {
//             backgroundColor: 'rgba(0, 0, 0, 0.8)',
//             padding: 12,
//             cornerRadius: 8,
//             callbacks: { label: ctx => `Score: ${ctx.raw}%` }
//           }
//         },
//         scales: {
//           y: {
//             min: 0,
//             max: 100,
//             ticks: { callback: value => `${value}%` },
//             grid: { color: 'rgba(0, 0, 0, 0.05)' }
//           },
//           x: { grid: { display: false } }
//         }
//       }}
//     />
//   ) : (
//     <div className="flex flex-col items-center justify-center h-full">
//       <i className="fas fa-chart-line text-5xl text-gray-300 mb-4"></i>
//       <p className="text-gray-500 font-medium">No performance data yet</p>
//       <p className="text-gray-400 text-sm">Complete your first assessment to see trends</p>
//     </div>
//   )}
// </div>


//         </div>

//         {/* Upcoming Assessments */}
//         <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
//           <div className="flex justify-between items-center mb-6">
//             <div>
//               <h3 className="text-xl font-bold text-gray-900">Upcoming</h3>
//               <p className="text-gray-600 text-sm">Tests & assignments</p>
//             </div>
//             <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
//               {upcomingAssessments.length} due
//             </span>
//           </div>
//           <div className="space-y-4">
//             {upcomingAssessments.length > 0 ? (
//               upcomingAssessments.slice(0, 4).map((assessment, index) => (
//                 <div key={index} className="p-4 border border-gray-200 rounded-xl hover:border-indigo-300 transition-colors">
//                   <div className="flex justify-between items-start">
//                     <div>
//                       <h4 className="font-bold text-gray-900">{assessment.subject_name}</h4>
//                       <p className="text-sm text-gray-600 mt-1">{assessment.type || 'Assessment'}</p>
//                       <div className="flex items-center space-x-3 mt-2">
//                         <span className="text-xs px-2 py-1 bg-gray-100 rounded-md">
//                           <i className="far fa-calendar mr-1"></i>
//                           {new Date(assessment.due_date).toLocaleDateString()}
//                         </span>
//                         <span className={`text-xs px-2 py-1 rounded-md ${
//                           assessment.priority === 'high' ? 'bg-red-100 text-red-800' :
//                           assessment.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
//                           'bg-blue-100 text-blue-800'
//                         }`}>
//                           {assessment.priority || 'normal'}
//                         </span>
//                       </div>
//                     </div>
//                     <button className="text-gray-400 hover:text-indigo-600 transition-colors">
//                       <i className="far fa-bell"></i>
//                     </button>
//                   </div>
//                 </div>
//               ))
//             ) : (
//               <div className="text-center py-8">
//                 <i className="fas fa-clipboard-check text-4xl text-gray-300 mb-3"></i>
//                 <p className="text-gray-500 font-medium">No upcoming assessments</p>
//                 <p className="text-gray-400 text-sm mt-1">Enjoy your free time!</p>
//               </div>
//             )}
//             {upcomingAssessments.length > 4 && (
//               <button className="w-full py-3 text-center text-indigo-600 hover:text-indigo-700 font-medium rounded-lg hover:bg-indigo-50 transition-colors">
//                 View all {upcomingAssessments.length} assessments
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Subject Performance */}
//       <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
//         <div className="p-6 border-b border-gray-100">
//           <div className="flex justify-between items-center">
//             <div>
//               <h3 className="text-xl font-bold text-gray-900">Subject Performance</h3>
//               <p className="text-gray-600 text-sm">Your scores across all subjects</p>
//             </div>
//             <div className="flex space-x-2">
//               <button className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
//                 This Term
//               </button>
//               <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors">
//                 All Time
//               </button>
//             </div>
//           </div>
//         </div>
        
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
//           {/* Subject Chart */}
//           <div>
//             <div className="h-72">
//               {subjectPerformances.length > 0 ? (
//                 <ChartComponent
//                   type="bar"
//                   data={getSubjectPerformanceData()}
//                   options={{
//                     responsive: true,
//                     maintainAspectRatio: false,
//                     plugins: {
//                       legend: {
//                         display: false
//                       },
//                       tooltip: {
//                         backgroundColor: 'rgba(0, 0, 0, 0.8)',
//                         padding: 12,
//                         cornerRadius: 8,
//                         callbacks: {
//                           label: (context) => `Score: ${context.raw}%`
//                         }
//                       }
//                     },
//                     scales: {
//                       y: {
//                         min: 0,
//                         max: 100,
//                         grid: {
//                           color: 'rgba(0, 0, 0, 0.05)'
//                         },
//                         ticks: {
//                           callback: (value) => `${value}%`
//                         }
//                       },
//                       x: {
//                         grid: {
//                           display: false
//                         }
//                       }
//                     }
//                   }}
//                 />
//               ) : (
//                 <div className="flex items-center justify-center h-full">
//                   <div className="text-center">
//                     <i className="fas fa-book text-5xl text-gray-300 mb-3"></i>
//                     <p className="text-gray-500 font-medium">No subject data available</p>
//                     <p className="text-gray-400 text-sm">Subjects will appear after enrollment</p>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Subject List */}
//           <div className="overflow-hidden rounded-xl border border-gray-200">
//             <div className="overflow-x-auto">
//               <table className="min-w-full divide-y divide-gray-200">
//                 <thead className="bg-gray-50">
//                   <tr>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Subject
//                     </th>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Score
//                     </th>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Grade
//                     </th>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Status
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="bg-white divide-y divide-gray-200">
//                   {subjectPerformances.slice(0, 6).map((subject, index) => {
//                     const gradeBadge = getGradeBadge(subject.latest_score);
//                     return (
//                       <tr key={index} className="hover:bg-gray-50 transition-colors">
//                         <td className="px-6 py-4">
//                           <div className="flex items-center">
//                             <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center">
//                               <i className="fas fa-book text-indigo-600"></i>
//                             </div>
//                             <div className="ml-4">
//                               <div className="text-sm font-semibold text-gray-900">
//                                 {subject.subject_name}
//                               </div>
//                               <div className="text-xs text-gray-500">
//                                 {subject.subject_code}
//                               </div>
//                             </div>
//                           </div>
//                         </td>
//                         <td className="px-6 py-4">
//                           <div className={`text-lg font-bold ${getGradeColor(subject.latest_score)}`}>
//                             {subject.latest_score?.toFixed(1) || 'N/A'}
//                             {subject.latest_score && '/100'}
//                           </div>
//                         </td>
//                         <td className="px-6 py-4">
//                           <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${gradeBadge.color}`}>
//                             {subject.latest_score ? gradeBadge.text : 'N/A'}
//                           </span>
//                         </td>
//                         <td className="px-6 py-4">
//                           <div className="flex items-center">
//                             <div className="h-2 w-16 bg-gray-200 rounded-full overflow-hidden">
//                               <div 
//                                 className={`h-full ${getGradeColor(subject.latest_score).replace('text-', 'bg-')}`}
//                                 style={{ width: `${subject.latest_score || 0}%` }}
//                               ></div>
//                             </div>
//                             <span className="ml-2 text-sm text-gray-500">
//                               {subject.total_assessments || 0} assessments
//                             </span>
//                           </div>
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>
//             {subjectPerformances.length > 6 && (
//               <div className="px-6 py-4 border-t border-gray-200">
//                 <button className="w-full py-2 text-center text-indigo-600 hover:text-indigo-700 font-medium rounded-lg hover:bg-indigo-50 transition-colors">
//                   Show all {subjectPerformances.length} subjects
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Quick Actions */}
//       <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-8 text-white">
//         <h3 className="text-2xl font-bold mb-6">Quick Access</h3>
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//           <Link

//           style={{ textDecoration: "none" }}

//             to="/student/grades"
//             className="group p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1"
//           >
//             <div className="flex items-start space-x-4">
//               <div className="p-3 bg-indigo-500/20 rounded-lg group-hover:bg-indigo-500/30 transition-colors">
//                 <i className="fas fa-graduation-cap text-2xl text-white"></i>
//               </div>
//               <div>
//                 <h4 className="text-lg font-bold mb-2">Detailed Grades</h4>
//                 <p className="text-white/80 text-sm">View complete academic history with detailed breakdowns</p>
//               </div>
//             </div>
//           </Link>
          
//           <Link

//           style={{ textDecoration: "none" }}

//             to="/student/grades#assessments"
//             className="group p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1"
//           >
//             <div className="flex items-start space-x-4">
//               <div className="p-3 bg-emerald-500/20 rounded-lg group-hover:bg-emerald-500/30 transition-colors">
//                 <i className="fas fa-chart-bar text-2xl text-white"></i>
//               </div>
//               <div>
//                 <h4 className="text-lg font-bold mb-2">Assessment Analytics</h4>
//                 <p className="text-white/80 text-sm">Deep dive into test scores and performance metrics</p>
//               </div>
//             </div>
//           </Link>
          
//           <Link

//           style={{ textDecoration: "none" }}
          
//             to="/student/profile"
//             className="group p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1"
//           >
//             <div className="flex items-start space-x-4">
//               <div className="p-3 bg-purple-500/20 rounded-lg group-hover:bg-purple-500/30 transition-colors">
//                 <i className="fas fa-user-cog text-2xl text-white"></i>
//               </div>
//               <div>
//                 <h4 className="text-lg font-bold mb-2">Profile Settings</h4>
//                 <p className="text-white/80 text-sm">Update personal information and preferences</p>
//               </div>
//             </div>
//           </Link>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default StudentDashboard;